import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { PageBody } from '../components/layout/PageBody';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { LoadingState } from '../components/common/LoadingState';
import { PasswordField } from '../components/common/PasswordField';
import { useFeedback } from '../components/common/FeedbackProvider';
import { useAuth } from '../hooks/useAuth';
import { useEngagement } from '../hooks/useEngagement';
import { authService } from '../services/authService';
import { PushNotificationsCard } from '../components/push/PushNotificationsCard';
import { activityLabel, roleLabel } from '../utils/labels';
import { tokens } from '../theme/tokens';

export function ProfilePage() {
  const { user, isStaff, applyViewer } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const { engagement, loading, error, setError } = useEngagement(Boolean(user));

  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [address, setAddress] = useState(user?.address ?? '');
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name ?? '');
    setPhone(user.phone ?? '');
    setAddress(user.address ?? '');
  }, [user]);

  if (!user) {
    return (
      <AppShell
        title="Profile"
        subtitle="Account and preferences — sign in to unlock more"
      >
        <PageBody>
          <EmptyState
            title="Browsing as guest"
            description="You can read the feed without an account. Sign in to like stories and get personal alerts."
            action={
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  component={RouterLink}
                  to="/login"
                  state={{ from: '/profile' }}
                  variant="contained"
                  color="secondary"
                >
                  Sign in
                </Button>
                <Button
                  component={RouterLink}
                  to="/signup"
                  state={{ from: '/profile' }}
                  variant="outlined"
                >
                  Create account
                </Button>
              </Stack>
            }
          />
        </PageBody>
      </AppShell>
    );
  }

  async function onSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const viewer = await authService.updateMe({
        name: name.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
      });
      applyViewer(viewer);
      if (viewer?.user) {
        setName(viewer.user.name ?? '');
        setPhone(viewer.user.phone ?? '');
        setAddress(viewer.user.address ?? '');
      }
      showSuccess('Profile updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not save profile');
    } finally {
      setSaving(false);
    }
  }

  async function onChangePassword(e) {
    e.preventDefault();
    if (newPassword.length < 6) {
      showError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSuccess('Password changed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not change password');
    } finally {
      setChangingPassword(false);
    }
  }

  return (
    <AppShell title="Profile" subtitle="Your account">
      <PageBody>
        <Card>
          <CardContent sx={{ p: { xs: 1.75, sm: 2.5, md: 3 } }}>
            <Stack
              direction="row"
              spacing={{ xs: 1.5, sm: 2 }}
              alignItems="center"
              sx={{ mb: { xs: 2, sm: 2.5 } }}
            >
              <Avatar
                sx={{
                  width: { xs: 52, sm: 64 },
                  height: { xs: 52, sm: 64 },
                  bgcolor: tokens.brandFill,
                  color: '#FFFFFF',
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 650,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  flexShrink: 0,
                }}
              >
                {user.name.slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    lineHeight: 1.3,
                    fontSize: { xs: '1.05rem', sm: '1.25rem' },
                  }}
                >
                  {user.name}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    mt: 0.25,
                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                  }}
                >
                  {user.email}
                </Typography>
                <Chip
                  size="small"
                  color={isStaff ? 'primary' : 'default'}
                  variant={isStaff ? 'filled' : 'outlined'}
                  label={roleLabel(user.role)}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Stack>

            <Box component="form" onSubmit={onSaveProfile}>
              <Stack spacing={2}>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Email"
                  fullWidth
                  value={user.email}
                  disabled
                  helperText="Contact an admin to change your email"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Phone"
                  fullWidth
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  InputLabelProps={{ shrink: Boolean(phone) || undefined }}
                />
                {isStaff ? (
                  <TextField
                    label="Address"
                    fullWidth
                    multiline
                    rows={2}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    InputLabelProps={{ shrink: Boolean(address) || undefined }}
                  />
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {saving ? 'Saving…' : 'Save profile'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 1.75, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 } }}>
              Change password
            </Typography>
            <Box component="form" onSubmit={onChangePassword}>
              <Stack spacing={2}>
                <PasswordField
                  label="Current password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <PasswordField
                  label="New password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  helperText="At least 6 characters"
                />
                <PasswordField
                  label="Confirm new password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={changingPassword}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {changingPassword ? 'Updating…' : 'Change password'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <PushNotificationsCard />

        {isStaff ? (
        <Card>
          <CardContent sx={{ p: { xs: 1.75, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 } }}>
              Your alert activity
            </Typography>
            <ErrorAlert message={error} onClose={() => setError(null)} />
            {loading && <LoadingState label="Loading…" />}
            {engagement && (
              <>
                <Grid
                  container
                  spacing={1.5}
                  sx={{ mb: engagement.recent?.length ? 2.5 : 0 }}
                >
                  {[
                    ['Opened', engagement.totals?.opens ?? 0],
                    ['Clicked', engagement.totals?.clicks ?? 0],
                    ['Reacted', engagement.totals?.reactions ?? 0],
                    ['Shared', engagement.totals?.shares ?? 0],
                  ].map(([label, value]) => (
                    <Grid item xs={6} sm={3} key={label}>
                      <Box
                        sx={{
                          height: '100%',
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                          {value}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {engagement.recent?.length > 0 && (
                  <Box>
                    <Typography fontWeight={700} sx={{ mb: 1.25 }}>
                      Recent
                    </Typography>
                    <Stack spacing={1} component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {engagement.recent.slice(0, 5).map((e) => (
                        <Typography
                          key={e.id}
                          component="li"
                          color="text.secondary"
                          variant="body2"
                          sx={{ lineHeight: 1.5 }}
                        >
                          <Box component="strong" sx={{ color: 'primary.dark' }}>
                            {activityLabel(e.type)}
                          </Box>
                          {e.notificationTitle
                            ? ` · ${e.notificationTitle}`
                            : ''}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
        ) : null}

        {isStaff && (
          <Card>
            <CardActionArea component={RouterLink} to="/admin" replace>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography fontWeight={700}>Staff tools</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Dashboard, content, and alerts
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        )}
      </PageBody>
    </AppShell>
  );
}
